import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createLeadReplyWithMaterials } from '@/services/api';
import type { CreateReplyWithMaterialsRequest, ReplyWithAttachmentsResponse } from '@/types';

interface UseLeadReplyWithMaterialsOptions {
  leadId: string;
  /** When set (prospect context), also invalidates prospect-scoped queries on success. */
  prospectId?: string;
  onSuccess?: (data: ReplyWithAttachmentsResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for creating a lead reply with marketing material attachments
 * Includes automatic query invalidation on success and idempotency key generation
 */
export function useLeadReplyWithMaterials({ leadId, prospectId, onSuccess, onError }: UseLeadReplyWithMaterialsOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateReplyWithMaterialsRequest) => {
      const idempotencyKey = crypto.randomUUID();
      return createLeadReplyWithMaterials(leadId, request, idempotencyKey);
    },
    onSuccess: (data) => {
      if (prospectId) {
        // Prospect context: refresh prospect thread, badge, and list.
        queryClient.invalidateQueries({ queryKey: ['prospect-replies', prospectId] });
        queryClient.invalidateQueries({ queryKey: ['prospect', prospectId] });
        queryClient.invalidateQueries({ queryKey: ['prospects'] });
      } else {
        // Lead context: refresh lead thread, detail, and list.
        queryClient.invalidateQueries({ queryKey: ['lead-replies', leadId] });
        queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
        queryClient.invalidateQueries({ queryKey: ['leads'] });
      }

      onSuccess?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
}
